import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { Playlist } from '../types/playlist';
import { toast } from '../lib/toast';

interface UsePlaylistsResult {
  playlists: Playlist[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  createPlaylist: (channelId: string, title: string, description?: string) => Promise<Playlist | null>;
  deletePlaylist: (playlist: Playlist) => Promise<void>;
  addVideo: (playlist: Playlist, videoId: string) => Promise<void>;
  removeVideo: (playlist: Playlist, videoId: string) => Promise<void>;
  /** Owner only — public playlists are visible to everyone who can see the channel. */
  setVisibility: (playlist: Playlist, isPublic: boolean) => Promise<void>;
}

/** `channelSlug` is the channel's own tenant (it may differ from the viewer's). */
export const usePlaylists = (channelHandle: string | undefined, channelSlug?: string): UsePlaylistsResult => {
  const { t } = useTranslation();
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!channelHandle) return;
    setLoading(true);
    const result = await apiClient.listPlaylists(channelHandle, channelSlug);
    if (result.data) setPlaylists(result.data as Playlist[]);
    else if (result.error) setError(result.error.message);
    setLoading(false);
  }, [channelHandle, channelSlug]);

  useEffect(() => { fetch(); }, [fetch]);

  const createPlaylist = useCallback(async (
    channelId: string,
    title: string,
    description?: string
  ): Promise<Playlist | null> => {
    const result = await apiClient.createPlaylist(channelId, title, description);
    if (result.data) {
      setPlaylists((prev) => [result.data as Playlist, ...prev]);
      return result.data as Playlist;
    }
    setError(result.error?.message ?? 'Error creating playlist');
    return null;
  }, []);

  const deletePlaylist = useCallback(async (playlist: Playlist) => {
    const result = await apiClient.deletePlaylist(playlist.id, playlist.channelId);
    if (result.error) {
      toast.error(t('playlist.delete_error', 'Could not delete the playlist.'));
      return;
    }
    setPlaylists((prev) => prev.filter((p) => p.id !== playlist.id));
    toast.success(t('playlist.deleted', 'Playlist deleted'));
  }, [t]);

  const addVideo = useCallback(async (playlist: Playlist, videoId: string) => {
    const result = await apiClient.addVideoToPlaylist(playlist.id, playlist.channelId, videoId);
    if (result.data) {
      setPlaylists((prev) => prev.map((p) => p.id === playlist.id ? result.data as Playlist : p));
    }
  }, []);

  const removeVideo = useCallback(async (playlist: Playlist, videoId: string) => {
    const result = await apiClient.removeVideoFromPlaylist(playlist.id, playlist.channelId, videoId);
    if (result.data) {
      setPlaylists((prev) => prev.map((p) => p.id === playlist.id ? result.data as Playlist : p));
    }
  }, []);

  const setVisibility = useCallback(async (playlist: Playlist, isPublic: boolean) => {
    const apply = (value: boolean) =>
      setPlaylists((prev) => prev.map((p) => p.id === playlist.id ? { ...p, isPublic: value } : p));
    apply(isPublic); // optimistic
    const result = await apiClient.setPlaylistVisibility(playlist.id, playlist.channelId, isPublic);
    if (result.error) {
      apply(!isPublic);
      toast.error(t('playlist.visibility_error', 'Could not change the playlist visibility.'));
      return;
    }
    toast.success(isPublic
      ? t('playlist.now_public', 'Playlist is now public')
      : t('playlist.now_private', 'Playlist is now private'));
  }, [t]);

  return { playlists, loading, error, refresh: fetch, createPlaylist, deletePlaylist, addVideo, removeVideo, setVisibility };
};
